import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useCallback,
  useImperativeHandle,
} from 'react';
import { Menu, RefreshCcw, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import BaseModal from './BaseModal';
import GameLost from './GameLost';
import GameRound from './GameRound';
import { Game } from '../utils/gameUtils';
import { useNavigate } from 'react-router-dom';
import Tooltip from './Tooltip';
import { playPressSound, playLoseSound } from '../utils/soundUtils';
import GameMode from './GameMode';

const GameMenu = forwardRef<GameMenuHandles, GameMenuProps>((props, ref) => {
  const {
    currentRound,
    onPause,
    onResume,
    onRestart,
    gameInstructions,
    roundProgress,
    onNavigationChange,
    hasPreviousGame,
    hasNextGame,
    onPrevious,
    onNext,
    isMenuOpen,
    onMenuToggle,
    isMenuVisible,
    hasLost,
    onExit,
    gameName,
    game,
    resetCounter,
    currentGameStats,
    sessionStartBest,
    onNavigateBack,
    isExpanded,
    onToggleExpand,
    onRoundProgressChange,
    onGameLost,
    setHasLost,
    isGameLoading,
    onGameModeChange,
    currentGameMode,
    isDarkMode,
  } = props;

  const [isAnimatingRestart, setIsAnimatingRestart] = useState(false);
  const [restartRotation, setRestartRotation] = useState(0);
  const [isRestartCooldown, setIsRestartCooldown] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(9);
  const cooldownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [countdownKey, setCountdownKey] = useState(0);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [hasGameStarted, setHasGameStarted] = useState(false);
  const [showPlayText, setShowPlayText] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNavigationBlocked, setIsNavigationBlocked] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  const COOLDOWN_DURATION = 5000;
  const NAVIGATION_BLOCK_DURATION = 1000;

  const navigate = useNavigate();

  useEffect(() => {
    if (game?.info?.defaultGameMode && typeof onGameModeChange === 'function') {
      if (
        !currentGameMode ||
        !game.info.gameModes?.some((mode) => mode.id === currentGameMode)
      ) {
        const lastGameMode = game.getLastGameMode?.();
        if (
          lastGameMode &&
          game.info.gameModes?.some((mode) => mode.id === lastGameMode)
        ) {
          onGameModeChange(lastGameMode);
        } else {
          onGameModeChange(game.info.defaultGameMode);
        }
      }
    }
  }, [gameName, game, onGameModeChange, currentGameMode]);

  useEffect(() => {
    onNavigationChange(isMenuOpen);
  }, [isMenuOpen, onNavigationChange]);

  useEffect(() => {
    if (currentRound > 0) {
      setHasGameStarted(true);
      setShowPlayText(false);
    }
  }, [currentRound]);

  useEffect(() => {
    setHasGameStarted(false);
    setShowPlayText(true);
  }, [gameName]);

  useEffect(() => {
    if (isModalOpen && currentRound > 0 && game?.info) {
      if (
        !currentGameMode &&
        game.info.defaultGameMode &&
        typeof onGameModeChange === 'function'
      ) {
        onGameModeChange(game.info.defaultGameMode);
      }
    }
  }, [isModalOpen, currentRound, currentGameMode, game, onGameModeChange]);

  const clearRestartCooldown = useCallback(() => {
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
      cooldownTimerRef.current = null;
    }
    setIsRestartCooldown(false);
    setIsCountingDown(false);
    setCooldownSeconds(9);
  }, []);

  const startCooldown = () => {
    clearRestartCooldown();

    setIsRestartCooldown(true);
    setCooldownSeconds(9);
    setIsCountingDown(true);
    setCountdownKey((prevKey) => prevKey + 1);

    const startTime = Date.now();
    const updateCooldown = () => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.ceil((COOLDOWN_DURATION - elapsed) / 1000);

      if (remaining <= 0) {
        clearRestartCooldown();
      } else {
        setCooldownSeconds(remaining);
      }
    };
    cooldownTimerRef.current = setInterval(updateCooldown, 100);
  };

  useImperativeHandle(ref, () => ({
    clearRestartCooldown,
    pauseAndOpenMenu: () => {
      onPause();
      onMenuToggle();
      setIsModalOpen(true);
    },
  }));

  const handlePrevious = () => {
    if (isNavigationBlocked) return;
    playPressSound();
    setIsNavigationBlocked(true);
    setTimeout(() => setIsNavigationBlocked(false), NAVIGATION_BLOCK_DURATION);

    clearRestartCooldown();
    setHasGameStarted(false);
    setShowPlayText(true);
    onRoundProgressChange(0);
    setIsNavigating(true);
    onPrevious();
  };

  const handleNext = () => {
    if (isNavigationBlocked) return;
    playPressSound();
    setIsNavigationBlocked(true);
    setTimeout(() => setIsNavigationBlocked(false), NAVIGATION_BLOCK_DURATION);

    clearRestartCooldown();
    setHasGameStarted(false);
    setShowPlayText(true);
    onRoundProgressChange(0);
    setIsNavigating(true);
    onNext();
  };

  const handleExitClick = () => {
    playPressSound();
    clearRestartCooldown();
    setHasGameStarted(false);
    setShowPlayText(true);
    onExit();
  };

  const handleRestartQuick = () => {
    if (isRestartCooldown) return;
    playPressSound();
    setIsAnimatingRestart(true);
    const nextRotation = restartRotation - 360;
    setRestartRotation(nextRotation);

    clearRestartCooldown();
    startCooldown();

    setTimeout(() => {
      setIsAnimatingRestart(false);
      onRestart();
    }, 200);
  };

  const handleMenuClose = () => {
    if (isNavigationBlocked) return;
    playPressSound();
    if (hasLost) {
      if (isRestartCooldown) return;
      clearRestartCooldown();
      startCooldown();
      onRestart();
      setHasGameStarted(false);
      setShowPlayText(true);
    } else {
      onMenuToggle();
      if (currentRound > 0) {
        setShowPlayText(false);
      }
      onResume();
    }
    setIsModalOpen(false);
    setIsNavigating(false);
  };

  const handleMenuToggleLocal = () => {
    playPressSound();
    if (!isModalOpen && currentRound > 0) {
      setShowPlayText(false);
    }
    onMenuToggle();
    setIsModalOpen(!isModalOpen);
    setIsNavigating(false);
  };

  const handlePlayClick = () => {
    playPressSound();
    if (!hasGameStarted) {
      setHasGameStarted(true);
      setShowPlayText(false);
      if (!currentGameMode && game?.info) {
        if (typeof onGameModeChange === 'function') {
          if (game.info.defaultGameMode) {
            onGameModeChange(game.info.defaultGameMode);
          } else if (game.info.gameModes && game.info.gameModes.length > 0) {
            onGameModeChange(game.info.gameModes[0].id);
          }
        }
      }
    }
    if (currentRound > 0) {
      setShowPlayText(false);
    } else {
      setShowPlayText(true);
    }
    onResume();
    handleMenuClose();
  };

  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    };
  }, []);

  const handleRestartMenuClick = () => {
    playPressSound();
    onRestart();
    setIsModalOpen(false);
  };

  const handleToggleExpand = () => {
    playPressSound();
    onToggleExpand();
  };

  const handleAbandonClick = () => {
    if (isNavigating) {
      playPressSound();
      onExit();
      setIsNavigating(false);
      return;
    }

    playLoseSound();
    onPause();
    setHasLost(true);

    if (typeof onGameLost === 'function' && game) {
      onGameLost(
        true,
        {
          customStat: currentGameStats.customStat,
          currentPairs: currentGameStats.customStat,
        },
        true
      );
    }

    setHasGameStarted(false);
    setShowPlayText(true);
    onRoundProgressChange(0);
    setIsModalOpen(true);
  };

  return (
    <div className="absolute top-0 left-0 w-full flex justify-center z-20">
      <div
        className={`relative ${
          isMenuVisible ? 'active' : ''
        } bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white py-2 px-3 rounded-b-md shadow-md inline-flex items-center game-menu-container transition-transform duration-500 transform ${
          isDarkMode ? 'bg-gradient-to-r from-yellow-500 to-red-500' : ''
        }`}
      >
        <div className="rounded-md flex items-center justify-center">
          <Tooltip content="Menú" position="far-left">
            <motion.button
              onClick={handleMenuToggleLocal}
              className="text-gray-500 dark:text-gray-400 p-2 rounded-full hover:bg-transparent dark:hover:bg-transparent transition-colors active:scale-95 transition-transform duration-100 hover:scale-125"
              aria-label="Abrir el menú"
              whileTap={{ scale: 0.9 }}
              whileHover={{ scale: 1.2 }}
            >
              <Menu className="w-5 h-5 transition-transform duration-500" />
            </motion.button>
          </Tooltip>
        </div>

        <GameRound
          currentRound={currentRound}
          roundProgress={roundProgress}
          hasLost={hasLost}
          restartCount={resetCounter}
        />

        <div className="rounded-md flex items-center justify-center relative">
          <Tooltip content="Reiniciar" position="far-right">
            <motion.button
              onClick={handleRestartQuick}
              disabled={isRestartCooldown}
              className={`text-gray-500 dark:text-gray-400 p-2 rounded-full hover:bg-transparent dark:hover:bg-transparent transition-colors ${
                isRestartCooldown
                  ? 'cursor-not-allowed opacity-50'
                  : 'active:scale-95 transition-transform duration-100 hover:scale-125'
              }`}
              aria-label="Reiniciar el juego"
              whileTap={{ scale: isRestartCooldown ? 1 : 0.9 }}
              whileHover={{ scale: isRestartCooldown ? 1 : 1.2 }}
            >
              <RefreshCcw
                className={`w-5 h-5 transition-transform duration-500 ${
                  isRestartCooldown
                    ? 'opacity-50 text-gray-400 dark:text-gray-500'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
                style={{ transform: `rotate(${restartRotation}deg)` }}
              />
              <AnimatePresence>
                {isRestartCooldown && (
                  <motion.span
                    key="cooldown"
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.5 }}
                    transition={{ duration: 0.2 }}
                    className="absolute -top-1 -right-1 bg-yellow-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full"
                  >
                    {cooldownSeconds}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
          </Tooltip>
        </div>

        <BaseModal isOpen={isModalOpen} onClose={handleMenuClose} maxWidth="md">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3 }}
          >
            <AnimatePresence mode="wait">
              {hasLost ? (
                <motion.div
                  key="game-lost"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <GameLost
                    currentRound={currentRound}
                    onRestart={handleRestartMenuClick}
                    onExit={handleExitClick}
                    isRestartCooldown={isRestartCooldown}
                    cooldownSeconds={cooldownSeconds}
                    countdownKey={countdownKey}
                    stats={
                      game?.getStats() || {
                        gamesPlayed: 0,
                        maxRound: 0,
                        customStat: 0,
                        gameName: '',
                      }
                    }
                    currentGameStats={currentGameStats}
                    customStatLabel={game?.info.customStatLabel || 'Acciones'}
                    customStatLabelSingular={
                      game?.info.customStatLabelSingular || 'Acción'
                    }
                    gameInstructions={gameInstructions}
                    gameName={gameName}
                    game={game!}
                    sessionStartBest={sessionStartBest}
                    currentGameMode={currentGameMode}
                    onGameModeChange={onGameModeChange}
                    isDarkMode={isDarkMode}
                    startCooldown={startCooldown}
                    clearRestartCooldown={clearRestartCooldown}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="game-menu"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="space-y-6">
                    <div className="text-gray-600 dark:text-gray-300">
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={gameName}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white text-center">
                            {`Com jugar a ${gameName}?`}
                          </h3>
                          {gameInstructions.map((paragraph, index) => (
                            <p key={index} className="mb-1 text-center">
                              {paragraph}
                            </p>
                          ))}
                        </motion.div>
                      </AnimatePresence>

                      <AnimatePresence mode="wait">
                        <motion.div
                          key={gameName + '-gamemode'}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <GameMode
                            game={game}
                            currentGameMode={currentGameMode}
                            onGameModeChange={onGameModeChange}
                            onRestart={handleRestartMenuClick}
                            startCooldown={startCooldown}
                            clearRestartCooldown={clearRestartCooldown}
                          />
                        </motion.div>
                      </AnimatePresence>

                      <div className="bg-gray-200 dark:bg-gray-700 p-4 mt-4 rounded-lg flex items-center justify-between">
                        <button
                          onClick={handlePrevious}
                          aria-label="Juego anterior"
                          disabled={!hasPreviousGame}
                          className={`nav-button -ml-2 ${
                            !hasPreviousGame
                              ? 'opacity-50 cursor-not-allowed'
                              : ''
                          }`}
                        >
                          <div className="icon-bg flex items-center justify-center">
                            <ArrowLeft className="text-white w-1/2 h-1/2" />
                          </div>
                        </button>

                        <div className="text-center flex-1 px-4 max-w-[300px] md:max-w-[400px]">
                          <h4 className="text-sm font-medium mb-2">
                            Descobreix més minijocs!
                          </h4>
                          <p className="text-sm hidden md:block">
                            Utilitza les fletxes laterals per explorar més
                            minijocs.
                          </p>
                          <p className="text-sm md:hidden">
                            Llisca el dit per la pantalla per explorar més
                            minijocs.
                          </p>
                        </div>

                        <button
                          onClick={handleNext}
                          aria-label="Siguiente juego"
                          disabled={!hasNextGame}
                          className={`nav-button rotate-180 -mr-2 ${
                            !hasNextGame ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <div className="icon-bg flex items-center justify-center">
                            <ArrowLeft className="text-white w-1/2 h-1/2" />
                          </div>
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col space-y-3 mt-6">
                      <div className="flex space-x-3">
                        <motion.button
                          onClick={handlePlayClick}
                          disabled={isGameLoading}
                          className={`bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-4 sm:py-2 sm:px-6 text-sm sm:text-base rounded-lg transition transform duration-300 flex items-center justify-center shadow-md hover:shadow-lg hover:scale-105 flex-1 ${
                            isGameLoading ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          whileTap={{ scale: isGameLoading ? 1 : 0.9 }}
                        >
                          <AnimatePresence mode="wait">
                            <motion.span
                              key={showPlayText ? 'play' : 'continue'}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              {showPlayText ? 'Jugar' : 'Continuar'}
                            </motion.span>
                          </AnimatePresence>
                        </motion.button>

                        <motion.button
                          onClick={handleAbandonClick}
                          className="bg-[#ee5711] hover:bg-[#cc4709] text-white font-bold py-2 px-4 sm:py-2 sm:px-6 text-sm sm:text-base rounded-lg transition transform duration-300 flex items-center justify-center shadow-md hover:shadow-lg hover:scale-105 flex-1"
                          whileTap={{ scale: 0.9 }}
                          whileHover={{ scale: 1.05 }}
                        >
                          <AnimatePresence mode="wait">
                            <motion.span
                              key={isNavigating ? 'sortir' : 'abandonar'}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              {isNavigating ? 'Sortir' : 'Abandonar'}
                            </motion.span>
                          </AnimatePresence>
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </BaseModal>
      </div>
    </div>
  );
});

export default GameMenu;
